import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  TextField,
  Autocomplete,
  Paper,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  SearchRounded as SearchIcon,
  CenterFocusStrongRounded as CenterIcon,
} from '@mui/icons-material';
import { GET_TREE_DATA } from '../apollo/queries';
import { getDisplayName, getInitials, getShortName, getPersonAvatarUrl } from '../hooks/usePersonHelpers';
import PersonNode from '../components/PersonNode';

const nodeTypes = { personNode: PersonNode };

function TreePage() {
  const navigate = useNavigate();
  const [selectedPersonId, setSelectedPersonId] = useState(null);

  const { data, loading, error } = useQuery(GET_TREE_DATA);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!data) return { initialNodes: [], initialEdges: [] };

    const persons = data.persons || [];
    const relationships = data.relationships || [];

    // Build relationship maps for quick lookups
    const parentChildRels = relationships.filter((r) => r.type === 'PARENT_CHILD');
    const spouseRels = relationships.filter((r) => r.type === 'SPOUSE' || r.type === 'PARTNER');

    // Maps for quick lookup
    const childToParents = new Map(); // childId -> [parentIds]
    const parentToChildren = new Map(); // parentId -> [childIds]
    const personToSpouses = new Map(); // personId -> [spouseIds]

    parentChildRels.forEach((rel) => {
      // Parent -> Child
      if (!parentToChildren.has(rel.fromPersonId)) {
        parentToChildren.set(rel.fromPersonId, []);
      }
      parentToChildren.get(rel.fromPersonId).push(rel.toPersonId);

      // Child -> Parents
      if (!childToParents.has(rel.toPersonId)) {
        childToParents.set(rel.toPersonId, []);
      }
      childToParents.get(rel.toPersonId).push(rel.fromPersonId);
    });

    spouseRels.forEach((rel) => {
      if (!personToSpouses.has(rel.fromPersonId)) {
        personToSpouses.set(rel.fromPersonId, []);
      }
      personToSpouses.get(rel.fromPersonId).push(rel.toPersonId);

      if (!personToSpouses.has(rel.toPersonId)) {
        personToSpouses.set(rel.toPersonId, []);
      }
      personToSpouses.get(rel.toPersonId).push(rel.fromPersonId);
    });

    // Find siblings (people who share at least one parent)
    const getSiblings = (personId) => {
      const parents = childToParents.get(personId) || [];
      const siblings = new Set();
      parents.forEach((parentId) => {
        const children = parentToChildren.get(parentId) || [];
        children.forEach((childId) => {
          if (childId !== personId) siblings.add(childId);
        });
      });
      return Array.from(siblings);
    };

    // Assign generations using BFS, ensuring spouses and siblings are same generation
    const personGeneration = new Map();
    const visited = new Set();

    // Find root people (no parents in the data)
    const hasParent = new Set();
    parentChildRels.forEach((rel) => hasParent.add(rel.toPersonId));
    const roots = persons.filter((p) => !hasParent.has(p.id)).map((p) => p.id);

    // BFS to assign generations
    const assignGenerations = (startId, startGen) => {
      const queue = [{ id: startId, gen: startGen }];

      while (queue.length > 0) {
        const { id, gen } = queue.shift();

        if (visited.has(id)) {
          // If already visited with a different generation, use the lower one
          // to keep spouses aligned
          continue;
        }

        visited.add(id);
        personGeneration.set(id, gen);

        // Spouses should be same generation
        const spouses = personToSpouses.get(id) || [];
        spouses.forEach((spouseId) => {
          if (!visited.has(spouseId)) {
            queue.push({ id: spouseId, gen });
          }
        });

        // Siblings should be same generation
        const siblings = getSiblings(id);
        siblings.forEach((siblingId) => {
          if (!visited.has(siblingId)) {
            queue.push({ id: siblingId, gen });
          }
        });

        // Children should be next generation
        const children = parentToChildren.get(id) || [];
        children.forEach((childId) => {
          if (!visited.has(childId)) {
            queue.push({ id: childId, gen: gen + 1 });
          }
        });

        // Parents should be previous generation
        const parents = childToParents.get(id) || [];
        parents.forEach((parentId) => {
          if (!visited.has(parentId)) {
            queue.push({ id: parentId, gen: gen - 1 });
          }
        });
      }
    };

    // Start from roots (people with no parents)
    roots.forEach((rootId) => {
      if (!visited.has(rootId)) {
        assignGenerations(rootId, 0);
      }
    });

    // Handle any disconnected people
    persons.forEach((person) => {
      if (!visited.has(person.id)) {
        assignGenerations(person.id, 0);
      }
    });

    // Normalize generations so minimum is 0
    const allGens = Array.from(personGeneration.values());
    const minGen = Math.min(...allGens, 0);
    if (minGen < 0) {
      persons.forEach((person) => {
        const current = personGeneration.get(person.id) || 0;
        personGeneration.set(person.id, current - minGen);
      });
    }

    // Group by generation
    const generations = new Map();
    persons.forEach((person) => {
      const gen = personGeneration.get(person.id) || 0;
      if (!generations.has(gen)) {
        generations.set(gen, []);
      }
      generations.get(gen).push(person);
    });

    // Layout constants
    const NODE_WIDTH = 180;
    const NODE_SPACING = 80;
    const GEN_HEIGHT = 200;
    const COUPLE_SPACING = 30;

    // Position tracking
    const personPositions = new Map();

    // Helper to get unit width
    const getUnitWidth = (members) =>
      members.length * NODE_WIDTH + (members.length - 1) * COUPLE_SPACING;

    // Get all generations sorted
    const sortedGensTopDown = Array.from(generations.keys()).sort((a, b) => a - b);
    const sortedGensBottomUp = Array.from(generations.keys()).sort((a, b) => b - a);

    // Build family units for each generation (couples grouped together)
    const genFamilyUnits = new Map();

    sortedGensTopDown.forEach((gen) => {
      const genPersons = generations.get(gen) || [];
      const familyUnits = [];
      const processedIds = new Set();

      genPersons.forEach((person) => {
        if (processedIds.has(person.id)) return;

        const unit = { members: [person], children: [], parents: [] };
        processedIds.add(person.id);

        // Add all spouses to the unit
        const spouses = personToSpouses.get(person.id) || [];
        spouses.forEach((spouseId) => {
          const spouse = genPersons.find((p) => p.id === spouseId);
          if (spouse && !processedIds.has(spouseId)) {
            unit.members.push(spouse);
            processedIds.add(spouseId);
          }
        });

        // Collect all children of this unit
        unit.members.forEach((member) => {
          const children = parentToChildren.get(member.id) || [];
          children.forEach((childId) => {
            if (!unit.children.includes(childId)) {
              unit.children.push(childId);
            }
          });
          // Collect parents
          const parents = childToParents.get(member.id) || [];
          parents.forEach((parentId) => {
            if (!unit.parents.includes(parentId)) {
              unit.parents.push(parentId);
            }
          });
        });

        familyUnits.push(unit);
      });

      genFamilyUnits.set(gen, familyUnits);
    });

    // PASS 1: Determine ORDER of units in each generation (top-down)
    // This ensures children are ordered based on their parents' order
    const orderedUnits = new Map();

    sortedGensTopDown.forEach((gen, genIndex) => {
      const familyUnits = genFamilyUnits.get(gen);

      if (genIndex === 0) {
        // Top generation - just use default order
        orderedUnits.set(gen, [...familyUnits]);
      } else {
        // Order based on parent positions in the ordered list above
        const parentGen = sortedGensTopDown[genIndex - 1];
        const parentOrder = orderedUnits.get(parentGen) || [];

        // Create a map of person -> order index based on parent units
        const personOrderIndex = new Map();
        parentOrder.forEach((parentUnit, idx) => {
          parentUnit.members.forEach((member) => {
            personOrderIndex.set(member.id, idx);
          });
        });

        // Sort child units by their parents' order
        const sortedUnits = [...familyUnits].sort((a, b) => {
          const aParentIdx = Math.min(
            ...a.parents.map((p) => personOrderIndex.get(p) ?? Infinity)
          );
          const bParentIdx = Math.min(
            ...b.parents.map((p) => personOrderIndex.get(p) ?? Infinity)
          );
          return aParentIdx - bParentIdx;
        });

        orderedUnits.set(gen, sortedUnits);
      }
    });

    // PASS 2: Position from BOTTOM up, so parents center above children
    sortedGensBottomUp.forEach((gen) => {
      const familyUnits = orderedUnits.get(gen);
      const y = gen * GEN_HEIGHT;

      // Calculate target X for each unit based on children's positions
      familyUnits.forEach((unit) => {
        if (unit.children.length > 0) {
          const childXs = unit.children
            .map((childId) => personPositions.get(childId)?.x)
            .filter((x) => x !== undefined);

          if (childXs.length > 0) {
            const minChildX = Math.min(...childXs);
            const maxChildX = Math.max(...childXs);
            unit.targetX = (minChildX + maxChildX) / 2;
          } else {
            unit.targetX = null;
          }
        } else {
          unit.targetX = null;
        }
      });

      // Position units, respecting their order but trying to hit targets
      const positionedRanges = [];

      // For bottom generation (no children), just space them evenly
      const hasAnyTargets = familyUnits.some((u) => u.targetX !== null);

      if (!hasAnyTargets) {
        // No targets - position sequentially
        let currentX = 0;
        familyUnits.forEach((unit) => {
          const unitWidth = getUnitWidth(unit.members);
          unit.members.forEach((person, memberIdx) => {
            const x = currentX + memberIdx * (NODE_WIDTH + COUPLE_SPACING) + NODE_WIDTH / 2;
            personPositions.set(person.id, { x, y });
          });
          positionedRanges.push({ start: currentX, end: currentX + unitWidth });
          currentX += unitWidth + NODE_SPACING;
        });
      } else {
        // Has targets - position at targets while avoiding collisions
        familyUnits.forEach((unit) => {
          const unitWidth = getUnitWidth(unit.members);
          let targetX = unit.targetX;

          // If no target, find a gap or place at end
          if (targetX === null) {
            if (positionedRanges.length > 0) {
              targetX = positionedRanges[positionedRanges.length - 1].end + NODE_SPACING + unitWidth / 2;
            } else {
              targetX = unitWidth / 2;
            }
          }

          let startX = targetX - unitWidth / 2;

          // Check for collisions and adjust minimally
          let hasCollision = true;
          let leftShift = 0;
          let rightShift = 0;

          while (hasCollision) {
            hasCollision = false;

            for (const range of positionedRanges) {
              const buffer = NODE_SPACING;
              if (startX < range.end + buffer && startX + unitWidth > range.start - buffer) {
                hasCollision = true;

                // Calculate how much we need to shift in each direction
                const shiftRight = range.end + buffer - startX;
                const shiftLeft = startX + unitWidth - (range.start - buffer);

                // Choose the smaller shift, but prefer staying close to target
                if (shiftRight <= shiftLeft) {
                  startX = range.end + buffer;
                  rightShift += shiftRight;
                } else {
                  startX = range.start - buffer - unitWidth;
                  leftShift += shiftLeft;
                }
                break;
              }
            }

            // Safety valve
            if (leftShift + rightShift > 10000) break;
          }

          // Record this range
          positionedRanges.push({ start: startX, end: startX + unitWidth });
          positionedRanges.sort((a, b) => a.start - b.start);

          // Position members
          unit.members.forEach((person, memberIdx) => {
            const x = startX + memberIdx * (NODE_WIDTH + COUPLE_SPACING) + NODE_WIDTH / 2;
            personPositions.set(person.id, { x, y });
          });
        });
      }
    });

    // Center the entire tree
    const allPositions = Array.from(personPositions.values());
    if (allPositions.length > 0) {
      const minX = Math.min(...allPositions.map((p) => p.x));
      const maxX = Math.max(...allPositions.map((p) => p.x));
      const centerOffset = (minX + maxX) / 2;

      allPositions.forEach((pos) => {
        pos.x -= centerOffset;
      });
    }

    // Create edges first so we know which handles are used
    const edges = relationships.map((rel, index) => {
      const isSpouseOrPartner = rel.type === 'SPOUSE' || rel.type === 'PARTNER';
      return {
        id: `${rel.id}-${index}`,
        source: rel.fromPersonId,
        target: rel.toPersonId,
        sourceHandle: isSpouseOrPartner ? 'right' : 'bottom',
        targetHandle: isSpouseOrPartner ? 'left' : 'top',
        type: isSpouseOrPartner ? 'straight' : 'smoothstep',
        style: {
          stroke: isSpouseOrPartner ? '#ff8f00' : '#01362E',
          strokeWidth: 2,
          strokeDasharray: isSpouseOrPartner ? '5,5' : undefined,
        },
        animated: false,
        label: rel.type === 'SPOUSE' ? '💍' : rel.type === 'PARTNER' ? '❤️' : undefined,
      };
    });

    // Calculate which handles are connected for each person
    const connectedHandles = new Map();
    edges.forEach((edge) => {
      if (!connectedHandles.has(edge.source)) {
        connectedHandles.set(edge.source, new Set());
      }
      connectedHandles.get(edge.source).add(edge.sourceHandle);

      if (!connectedHandles.has(edge.target)) {
        connectedHandles.set(edge.target, new Set());
      }
      connectedHandles.get(edge.target).add(edge.targetHandle);
    });

    // Create nodes from positions
    const nodes = persons.map((person) => {
      const pos = personPositions.get(person.id) || { x: 0, y: 0 };
      const handles = connectedHandles.get(person.id) || new Set();
      return {
        id: person.id,
        type: 'personNode',
        position: pos,
        data: {
          person,
          selected: person.id === selectedPersonId,
          showTop: handles.has('top'),
          showBottom: handles.has('bottom'),
          showLeft: handles.has('left'),
          showRight: handles.has('right'),
        },
      };
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [data, selectedPersonId]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes when data changes
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const handleNodeClick = useCallback((_, node) => {
    setSelectedPersonId(node.id);
  }, []);

  const handleNodeDoubleClick = useCallback((_, node) => {
    navigate(`/person/${node.id}`);
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading tree: {error.message}</Alert>;
  }

  const persons = data?.persons || [];

  return (
    <Box sx={{
      height: {
        xs: 'calc(100dvh - 160px)', // Mobile: account for app bar and bottom nav
        md: 'calc(100vh - 140px)'   // Desktop: just app bar and padding
      },
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Autocomplete
          options={persons}
          getOptionLabel={(option) => getDisplayName(option)}
          onChange={(_, value) => {
            if (value) {
              setSelectedPersonId(value.id);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search for a person..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          )}
          renderOption={(props, person) => (
            <Box component="li" {...props} key={person.id}>
              <Avatar src={person.profilePictureUrl} sx={{ mr: 2, width: 24, height: 24, fontSize: 12 }}>
                {getInitials(person)}
              </Avatar>
              {getDisplayName(person)}
            </Box>
          )}
          sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 400 } }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}
        >
          Double-click a node to view details
        </Typography>
      </Box>

      <Paper sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#aaa" gap={16} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </Paper>

      {selectedPersonId && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 80,
            right: 24,
            p: 2,
            maxWidth: 300,
            zIndex: 10,
          }}
        >
          {(() => {
            const person = persons.find((p) => p.id === selectedPersonId);
            if (!person) return null;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={getPersonAvatarUrl(person, 48)} sx={{ width: 48, height: 48 }}>
                  {getInitials(person)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {getDisplayName(person)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/person/${person.id}`)}
                  >
                    View Profile →
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}

export default TreePage;

